import React, {
    useEffect,
    useMemo,
    useCallback,
    useState,
    useRef,
    FC
} from "react"
import { push, replace } from "connected-react-router"
import { useDispatch } from "react-redux"
import { Resizable, ResizeCallback } from "re-resizable"
import {
    KeyboardShortcut,
    load,
    LocalStorageKey,
    UpdateReturn,
    update,
    hasClassificationFieldSchema
} from "resha"
import { SkipNavigationLink } from "dash-styled"

import {
    APIJobQueryModel,
    APIJobModel,
    JobStatus,
    RelabelJobResponse,
    JobIdsOfBatchResponse,
    JobVersionItem,
    isRelabelableJob,
    JobDialog
} from "../../../../../../Models/API/JobModel"
import {
    hasValue,
    FetchState,
    getValue,
    hasResult
} from "../../../../../../Models/Client/FetchState"
import { FilterAndSortExpression } from "../../../../../../Models/Client/FilterAndSortExpression"
import { SortExpression } from "../../../../../../Models/Client/SortExpression"
import { FilterExpression } from "../../../../../../Models/Client/FilterExpression"
import { DataAccess } from "../../../../../../Models/Client/DataAccess"
import { hasAppPermission } from "../../../../../../Models/Client/ProjectRole"
import { getItemList } from "../../../../../../Models/Client/RemoteData"
import { AppInstanceStatus } from "../../../../../../Models/API/AppInstanceModel"

import { LoadingContainer } from "../../../../../Common/LoadingContainer/LoadingContainer"
import { FilterFields } from "../../../../../Common/FilterComposer/FilterComposer"
import { NothingMatched } from "../../../../../Common/Empty/NothingMatched"
import { NavPrompt } from "../../../../../Common/NavPrompt/NavPrompt"
import { DataPointVersionListItem } from "../../../Common/DataPointVersionListItem/DataPointVersionListItem"

import { useJobListNavigation } from "../../../../../../Hooks/useJobListNavigation"
import { useJob } from "../../../../../../Hooks/useJob"
import { useAppTemplate } from "../../../../../../Hooks/useAppTemplate"
import { useJobDownload } from "../../../../../../Hooks/useJobDownload"
import { useAppInstance } from "../../../../../../Hooks/useAppInstance"
import { useAssumedRoles } from "../../../../../../Hooks/useAssumedRoles"
import { useAppDetailsPaths } from "../../../../../../Hooks/useAppDetailsPaths"
import { useUrlSigner } from "../../../../../../Hooks/useUrlSigner"
import { useJobHistory } from "../../../../../../Hooks/useJobHistory"
import { useJobVersionResponse } from "../../../../../../Hooks/useJobVersionResponse"
import { useErrorTrackingInfo } from "../../../../../../Hooks/useErrorTrackingInfo"
import { useAppSchema } from "../../../../../../Hooks/useAppSchema"
import { usePrelabels } from "../../../../../../Hooks/usePrelabels"
import { useBoolean } from "../../../../../../Hooks/useBoolean"
import { useTaskDetailsPaths } from "../../../../../../Hooks/useTaskDetailsPaths"
import { useAppState } from "../../../../../../Hooks/useReduxState"
import { DownloadType } from "../../../../../../Hooks/useJobListDownload"
import { useJobResponse } from "../../../../../../Hooks/useJobResponse"

import { TrackingType } from "../../../../../../Actions/Types"
import {
    cancelJob,
    enqueueJob,
    relabelJob,
    addTagByJobID,
    removeTagByJobID,
    deleteJob,
    stopJobIdsPoll,
    startJobIdsPoll,
    startJobIdsOfBatchPoll,
    stopJobIdsOfBatchPoll,
    markJobCorrectness,
    updateJobOutput
} from "../../../../../../Actions/API/Job/JobActions"
import {
    reloadTags,
    startMonitorApp,
    stopMonitorApp
} from "../../../../../../Actions/API/AppInstance/AppInstanceActions"
import { dirtinessChanged } from "../../../../../../Actions/Client/InputRender/InputRenderActions"
import { warnMessage } from "../../../../../../Actions/Client/MessagesActions"

import { MainThunkDispatch } from "../../../../../../Reducers"
import { DataAccessContext } from "../../../../../../Context/DataAccessContext"
import { routePaths } from "../../../../../../Routes"
import { findImageUrlPathForDownload } from "../../../../../../Helpers/Schema/Schema"
import { DetailViewFooter } from "../DetailView/Footer/DetailViewFooter"
import {
    jobSortOptions,
    defaultJobFilterOnAdd,
    jobWithMessagesSortOptions
} from "../Common/constants"
import { JobBase } from "../Common/JobBase"
import { JobPanelNavHeader } from "./NavHeader"
import { JobDetailViewHeader } from "./Header"
import { JobDetailViewSidebar } from "./Sidebar"
import {
    JobDataDetailView,
    JobDataDetailViewProps,
    JobDataViewTabs
} from "./DataView"

interface JobDetailProps {
    orgUsername: string
    appID: string
    filterAndSort: FilterAndSortExpression<APIJobQueryModel>
    /**
     * Identifier of the job to show.
     * - `undefined` or `"loading"` means the job to show is not decided yet.
     * - `Result.err` means there is nothing to show.
     * - `Result.ok` encapsulates the identifier of the job to show
     */
    jobIDFetch: FetchState<string>
    versionFetch: FetchState<string>
    clearFilterOnCloseModal: boolean
    navigationItems?: APIJobModel[]
    filterFields: FilterFields<APIJobQueryModel & { output: unknown }>
    isReviewView: boolean
    dataTab?: JobDataViewTabs
    onClearSelection: () => void
}

export const JobDetail: FC<JobDetailProps> = ({
    orgUsername,
    appID,
    jobIDFetch,
    versionFetch,
    filterAndSort,
    clearFilterOnCloseModal,
    navigationItems,
    filterFields,
    isReviewView,
    dataTab,
    onClearSelection
}) => {
    const jobID = getValue(jobIDFetch) ?? undefined
    const version = getValue(versionFetch) ?? undefined

    useErrorTrackingInfo("superai.jobID", jobID)

    const dispatch = useDispatch<MainThunkDispatch>()
    const rolesFetch = useAssumedRoles(appID)
    const templateFetch = useAppTemplate(appID)
    const instanceFetch = useAppInstance(appID)
    const schemaFetch = useAppSchema(appID, false)
    const jobFetch = useJob(appID, jobID)
    const [shortcutList, setShortcutList] =
        useState<{ [key: string]: [string, KeyboardShortcut][] } | undefined>()
    const prelabels = usePrelabels(appID, jobID)

    const [selectedVersion, setVersion] = useState<number>(0)
    const [isMessagesPath, appDetailPath, detailPath] = useAppDetailsPaths()

    const [hasReviewChanged, setReviewChange] = useState<boolean>(false)
    const item = useRef<UpdateReturn<unknown | { formData: unknown }>>()
    const [isHighlightEdit, highlightEdit] = useBoolean(false)

    const navigate = useCallback(path => dispatch(push(path)), [dispatch])
    const hasDirtyForm = useAppState(
        state => state.client.inputRender.hasDirtyForm
    )
    const shouldBlock = useCallback(() => hasDirtyForm, [hasDirtyForm])

    // poll changes in app
    const instanceStatus = getValue(instanceFetch)?.status
    useEffect(() => {
        if (instanceStatus === AppInstanceStatus.ENABLED) {
            dispatch(startMonitorApp(appID))
            return () => {
                dispatch(stopMonitorApp())
            }
        }
    }, [instanceStatus, dispatch, appID])

    // dispatch event when job viewed
    useEffect(() => {
        if (!hasValue(jobFetch)) return

        dispatch({
            type: TrackingType.job_view,
            payload: jobFetch.value
        })
    }, [jobFetch, dispatch])

    useEffect(() => {
        if (version !== undefined) {
            setVersion(parseInt(version))
        }
    }, [version])

    useEffect(() => {
        dispatch(dirtinessChanged(hasReviewChanged))
    }, [hasReviewChanged, dispatch])

    const [isJobRelabeled, setIsRelabeledTrue, setIsRelabeledFalse] =
        useBoolean(false)

    const isJobRelabeledRef = useRef(false)

    useEffect(() => {
        isJobRelabeledRef.current = isJobRelabeled
    }, [isJobRelabeled])

    const relabelJobCleanup = useCallback(() => {
        isJobRelabeledRef.current = false
        setIsRelabeledFalse()
        dispatch(stopJobIdsPoll())
        dispatch(stopJobIdsOfBatchPoll())
    }, [dispatch, setIsRelabeledFalse])

    const jobResponseFetch = useJobResponse(appID, jobID)
    const jobHistoryFetch = useJobHistory(jobID)
    const jobVersionFetch = useJobVersionResponse(appID, jobID, selectedVersion)

    const jobVersion = getValue(jobVersionFetch)
    const navigation = useJobListNavigation(
        orgUsername,
        appID,
        { ...filterAndSort, clearFilterOnCloseModal, dataTab },
        jobID,
        navigationItems,
        isMessagesPath,
        isReviewView
    )

    const [isDownloading, setDownloading] = useState(false)
    const [dialog, setDialog] = useState<JobDialog>(undefined)
    const hideDialog = useCallback(() => {
        setDialog(undefined)
        setDownloading(false)
    }, [])
    const confirmRelabel = useCallback(() => setDialog("relabel"), [])
    const confirmDelete = useCallback(() => setDialog("delete"), [])
    const [downloadType, setDownloadType] = useState<DownloadType>("json")

    const handleNavPrev = useCallback(() => {
        if (navigation?.onPrev) {
            setVersion(0)
            navigation?.onPrev()
        }
    }, [navigation])
    const handleNavNext = useCallback(() => {
        if (navigation?.onNext) {
            setVersion(0)
            navigation?.onNext()
        }
    }, [navigation])

    const handleReviewView = useCallback(
        (reviewView: boolean) => {
            if (jobID !== undefined) {
                if (reviewView) {
                    dispatch(
                        push(
                            isMessagesPath
                                ? routePaths.jobsMessageReview(
                                      orgUsername,
                                      appID,
                                      jobID,
                                      { ...filterAndSort, dataTab }
                                  )
                                : routePaths.jobsReview(
                                      orgUsername,
                                      appID,
                                      jobID,
                                      { ...filterAndSort, dataTab }
                                  )
                        )
                    )
                    return
                }
                dispatch(
                    replace(
                        detailPath(orgUsername, appID, jobID, {
                            ...filterAndSort,
                            dataTab
                        })
                    )
                )
            }
        },
        [
            dispatch,
            orgUsername,
            appID,
            jobID,
            filterAndSort,
            dataTab,
            isMessagesPath,
            detailPath
        ]
    )

    const handleClose = useCallback(
        () =>
            isReviewView
                ? handleReviewView(false)
                : dispatch(
                      push(
                          appDetailPath(
                              orgUsername,
                              appID,
                              clearFilterOnCloseModal
                                  ? { ...filterAndSort, filter: [] }
                                  : filterAndSort,
                              undefined,
                              jobID
                          )
                      )
                  ),
        [
            appDetailPath,
            appID,
            jobID,
            clearFilterOnCloseModal,
            dispatch,
            filterAndSort,
            orgUsername,
            handleReviewView,
            isReviewView
        ]
    )

    const handleRelabeledJobIdFetch = useCallback(
        (jobData: APIJobModel) => {
            if (jobData !== undefined && isJobRelabeledRef.current) {
                dispatch(
                    replace(
                        routePaths.jobsDetail(
                            orgUsername,
                            jobData.appId,
                            jobData.id,
                            filterAndSort
                        )
                    )
                )
            }
        },
        [dispatch, filterAndSort, orgUsername]
    )

    const handleCorrectnessChange = useCallback(
        async (isCorrect: boolean, message?: string) => {
            if (jobID !== undefined) {
                await dispatch(markJobCorrectness(appID, jobID, isCorrect))
            }
        },
        [appID, jobID, dispatch]
    )

    const handleRelabel = useMemo(() => {
        if (jobID === undefined) return undefined
        return () => {
            setIsRelabeledTrue()
            setVersion(0)
            return dispatch(relabelJob(jobID, false))
                .then((relabelResponse: RelabelJobResponse | undefined) => {
                    if (relabelResponse) {
                        dispatch(
                            startJobIdsOfBatchPoll(
                                appID,
                                relabelResponse.batch_id
                            )
                        ).then((jobIdsResponse: JobIdsOfBatchResponse) => {
                            dispatch(stopJobIdsOfBatchPoll())
                            const newJobUUID = jobIdsResponse.job_ids[0]
                            if (newJobUUID !== undefined) {
                                dispatch(startJobIdsPoll(newJobUUID)).then(
                                    (payload: APIJobModel) => {
                                        handleRelabeledJobIdFetch(payload)
                                        setIsRelabeledFalse()
                                    }
                                )
                            }
                        })
                    }
                })
                .finally(hideDialog)
        }
    }, [
        appID,
        dispatch,
        handleRelabeledJobIdFetch,
        hideDialog,
        jobID,
        setIsRelabeledFalse,
        setIsRelabeledTrue
    ])
    const handleCancel = useMemo(
        () =>
            jobID === undefined
                ? undefined
                : () =>
                      dispatch(cancelJob(appID, jobID, false)).finally(
                          hideDialog
                      ),
        [hideDialog, dispatch, appID, jobID]
    )
    const handleDelete = useMemo(
        () =>
            jobID === undefined
                ? undefined
                : () =>
                      dispatch(deleteJob(appID, jobID, false)).finally(() => {
                          if (navigation?.onNext) navigation?.onNext()
                          else if (navigation?.onPrev) navigation?.onPrev()
                          else handleClose()
                          hideDialog()
                      }),
        [hideDialog, dispatch, appID, jobID, handleClose, navigation]
    )
    const handleEnqueue = useMemo(
        () =>
            jobID === undefined
                ? undefined
                : () =>
                      dispatch(enqueueJob(appID, jobID, false)).finally(
                          hideDialog
                      ),
        [hideDialog, dispatch, jobID, appID]
    )
    const handleChangeSort = (sort: SortExpression<APIJobQueryModel>) =>
        dispatch(
            push(
                appDetailPath(orgUsername, appID, {
                    ...filterAndSort,
                    openDetail: true,
                    sort
                })
            )
        )
    const handleChangeFilter = (filter: FilterExpression<APIJobQueryModel>[]) =>
        dispatch(
            push(
                appDetailPath(orgUsername, appID, {
                    ...filterAndSort,
                    openDetail: true,
                    filter
                })
            )
        )
    const handleAddTag = useMemo(
        () =>
            jobID !== undefined &&
            hasValue(jobFetch) &&
            hasAppPermission(getValue(rolesFetch) ?? [], "job:edit-tags")
                ? async (tag: string, isNew: boolean) => {
                      await dispatch(addTagByJobID(appID, jobID, tag))

                      if (isNew) {
                          dispatch(reloadTags(appID))
                      }
                  }
                : undefined,
        [appID, jobID, dispatch, jobFetch, rolesFetch]
    )
    const handleRemoveTag = useMemo(
        () =>
            jobID !== undefined &&
            hasValue(jobFetch) &&
            hasAppPermission(getValue(rolesFetch) ?? [], "job:edit-tags")
                ? (tag: string) => dispatch(removeTagByJobID(appID, jobID, tag))
                : undefined,
        [appID, jobID, dispatch, jobFetch, rolesFetch]
    )

    const handleItemChange = useCallback(
        (newItem: UpdateReturn<unknown> | undefined) => {
            item.current = newItem
        },
        []
    )

    useEffect(() => {
        relabelJobCleanup()

        return () => {
            relabelJobCleanup()
        }
    }, [jobID, relabelJobCleanup])

    const sortOptions = {
        applied: filterAndSort.sort,
        fields: isMessagesPath ? jobWithMessagesSortOptions : jobSortOptions,
        onChange: handleChangeSort
    }
    const filterOptions = {
        applied: filterAndSort.filter,
        fields: filterFields,
        defaultOnAdd: defaultJobFilterOnAdd,
        onChange: handleChangeFilter
    }

    const versionsList = useMemo(() => {
        if (!hasValue(jobHistoryFetch)) {
            return undefined
        }

        const jobHistory = jobHistoryFetch.value
        const indexOffset = isJobRelabeled ? 1 : 0
        const totalVersions =
            (jobHistory !== undefined ? jobHistory.total + 1 : 1) + indexOffset
        const job = getValue(jobFetch)
        const jobResponse = getValue(jobResponseFetch)

        let payload: JobVersionItem[] = []

        if (job !== undefined) {
            const oldVersions =
                jobHistory === undefined
                    ? []
                    : jobHistory.history.map((versionItem, index) => {
                          const versionName = `Version ${versionItem.version}`
                          const creationDate = versionItem.jobCreatedEdited
                              ? new Date(versionItem.jobCreatedEdited)
                              : null
                          const jobResponse =
                              index === selectedVersion - 1 &&
                              jobVersion !== undefined
                                  ? {
                                        response: jobVersion.response,
                                        responseData: jobVersion.responseData
                                    }
                                  : {
                                        response: undefined,
                                        responseData: undefined
                                    }

                          return {
                              jobID: job.id,
                              versionJobId: `${versionItem.versionJobId}`,
                              originalJobId: `${versionItem.jobId}`,
                              value: index + indexOffset + 1,
                              versionName,
                              correct: versionItem.correct,
                              status: versionItem.status,
                              created: creationDate,
                              isLatestVersion: false,
                              ...jobResponse,
                              label: (
                                  <DataPointVersionListItem
                                      created={creationDate}
                                      correct={versionItem.correct}
                                      status={versionItem.status}
                                      version={versionName}
                                      jobType={job.jobType}
                                  />
                              )
                          }
                      })

            const relabeledVersion = {
                jobID: "",
                versionJobId: "",
                originalJobId: "",
                value: 0,
                versionName: "Latest Version",
                correct: false as null | boolean,
                status: JobStatus.IN_CREATION,
                created: new Date() as Date | null,
                isLatestVersion: false,
                response: null as unknown,
                responseData: null as unknown,
                label: (
                    <DataPointVersionListItem
                        created={new Date()}
                        correct={false}
                        status={JobStatus.IN_CREATION}
                        version={"Latest Version"}
                        jobType={job.jobType}
                    />
                )
            }

            const currentJobCreationDate =
                jobHistory !== undefined &&
                jobHistory.history.length &&
                jobHistory.history[0].created !== null
                    ? new Date(jobHistory.history[0].created)
                    : job.created === ""
                    ? null
                    : new Date(job.created)

            const currentJobVersionName = isJobRelabeled
                ? `Version ${totalVersions - 1}`
                : "Latest Version"

            const latestVersion = {
                jobID: job.id,
                versionJobId: job.id,
                originalJobId: job.id,
                value: 0 + indexOffset,
                versionName: currentJobVersionName,
                correct: job.correct,
                status: job.status,
                created: currentJobCreationDate,
                isLatestVersion: true,
                response: jobResponse?.response,
                responseData: jobResponse?.responseData,
                label: (
                    <DataPointVersionListItem
                        created={currentJobCreationDate}
                        correct={job.correct}
                        status={job.status}
                        version={currentJobVersionName}
                        jobType={job.jobType}
                    />
                )
            }

            payload = [latestVersion].concat(oldVersions)

            if (isJobRelabeled) {
                payload = [relabeledVersion].concat(payload)
            }
        }

        return payload
    }, [
        jobHistoryFetch,
        isJobRelabeled,
        jobFetch,
        jobResponseFetch,
        selectedVersion,
        jobVersion
    ])

    const dataAccess = useMemo(() => {
        if (jobID === undefined || versionsList === undefined) {
            return DataAccess.owner
        }

        const originalJobIdOfSelectedVersion =
            versionsList[selectedVersion]?.originalJobId

        if (originalJobIdOfSelectedVersion === undefined) {
            return DataAccess.perJob(jobID)
        }

        return DataAccess.perJob(originalJobIdOfSelectedVersion)
    }, [jobID, selectedVersion, versionsList])
    const urlSigner = useUrlSigner(dataAccess)

    const imageDownloadPath = useMemo(
        () =>
            findImageUrlPathForDownload(getValue(templateFetch)?.outputSchema),
        [templateFetch]
    )

    const allowResponseDataDownload = useMemo(
        () =>
            imageDownloadPath !== undefined ||
            getValue(templateFetch)?.metadata.post_process_data === "available",
        [imageDownloadPath, templateFetch]
    )

    const onDownload = useJobDownload(
        urlSigner,
        appID,
        isJobRelabeled,
        selectedVersion,
        jobID,
        downloadType === "response_data" ? imageDownloadPath : undefined,
        downloadType === "response_data" &&
            getValue(templateFetch)?.metadata.post_process_data === "available"
    )

    const handleDownload = useCallback(async () => {
        setDownloading(true)
        try {
            await onDownload?.()
        } finally {
            hideDialog()
        }
    }, [onDownload, hideDialog])
    const [, taskDetailPath] = useTaskDetailsPaths()
    const [, , jobDetailPath] = useAppDetailsPaths()
    const handleTaskOnChange = useCallback(
        (newTaskID: string | undefined) => {
            if (versionsList === undefined) {
                return
            }
            if (newTaskID !== undefined) {
                dispatch(
                    push(
                        taskDetailPath(
                            orgUsername,
                            appID,
                            versionsList[selectedVersion].jobID,
                            String(selectedVersion),
                            newTaskID,
                            filterAndSort
                        )
                    )
                )
            } else {
                dispatch(
                    push(
                        jobDetailPath(
                            orgUsername,
                            appID,
                            versionsList[selectedVersion].jobID,
                            { ...filterAndSort, dataTab }
                        )
                    )
                )
            }
        },
        [
            appID,
            dispatch,
            filterAndSort,
            dataTab,
            orgUsername,
            selectedVersion,
            taskDetailPath,
            jobDetailPath,
            versionsList
        ]
    )

    const currentJob = versionsList?.[selectedVersion]

    const taskListData = useAppState(state => {
        if (
            hasValue(jobFetch) &&
            jobFetch.value.id !== undefined &&
            currentJob !== undefined
        ) {
            return state.api.tasks.table[currentJob.versionJobId]
        } else {
            return
        }
    })
    const taskList = useMemo(
        () => (taskListData !== undefined ? getItemList(taskListData) : []),
        [taskListData]
    )

    const onEditOutput = useMemo(() => {
        if (
            hasValue(jobFetch) &&
            jobFetch.value.status === JobStatus.COMPLETED &&
            getValue(templateFetch)?.metadata.edit_output !== "not_allowed" &&
            hasAppPermission(getValue(rolesFetch) ?? [], "job:edit")
        ) {
            return () =>
                dispatch(
                    push(
                        routePaths.jobEdit(
                            orgUsername,
                            appID,
                            jobFetch.value.id,
                            {
                                clearFilterOnCloseModal,
                                ...filterAndSort,
                                dataTab
                            }
                        ),
                        {
                            isMessagesPath
                        }
                    )
                )
        } else {
            return undefined
        }
    }, [
        jobFetch,
        templateFetch,
        rolesFetch,
        dispatch,
        orgUsername,
        appID,
        clearFilterOnCloseModal,
        filterAndSort,
        isMessagesPath,
        dataTab
    ])

    const handleReviewChange = useCallback(() => {
        setReviewChange(true)
    }, [])

    const handleReviewAction = useCallback(
        async (save: boolean) => {
            if (
                !hasValue(templateFetch) ||
                !hasValue(instanceFetch) ||
                !hasValue(jobFetch) ||
                jobID === undefined
            )
                return
            if (save) {
                if (item.current !== undefined) {
                    const { item: reviewedItem, valid } = await (
                        await import("resha")
                    ).executeExport(item.current)
                    if (
                        !valid ||
                        typeof reviewedItem !== "object" ||
                        reviewedItem === null ||
                        !("formData" in reviewedItem)
                    ) {
                        dispatch(warnMessage("The review is invalid"))
                        return
                    }
                    await dispatch(
                        updateJobOutput(
                            appID,
                            jobFetch.value.uuid,
                            jobID,
                            (reviewedItem as any).formData,
                            instanceFetch.value.outputSchema ??
                                templateFetch.value.outputSchema,
                            `jobs/${jobID}/output`,
                            true,
                            false,
                            false
                        )
                    )
                }
                dispatch(dirtinessChanged(false))
            }

            handleReviewView(false)
        },
        [
            appID,
            dispatch,
            instanceFetch,
            jobFetch,
            jobID,
            templateFetch,
            handleReviewView
        ]
    )

    const handleChangeDataTab = useCallback(
        (dataTab: JobDataViewTabs) => {
            if (jobID !== undefined) {
                dispatch(
                    replace(
                        detailPath(orgUsername, appID, jobID, {
                            ...filterAndSort,
                            dataTab
                        })
                    )
                )
            }
        },
        [orgUsername, appID, jobID, filterAndSort, detailPath, dispatch]
    )

    if (!hasValue(schemaFetch)) {
        return null
    }

    const isClassificationApp = hasClassificationFieldSchema(
        schemaFetch.value.output.uiSchema
    )

    const userSettings = JSON.parse(
        load(LocalStorageKey.USER_LOCAL_SETTINGS) || "{}"
    )

    const jobReviewPanelWidth = userSettings?.jobReviewPanelWidth
        ? userSettings?.jobReviewPanelWidth
        : isClassificationApp
        ? "25%"
        : "50%"

    const handlePanelResize: ResizeCallback = (
        event,
        direction,
        refToElement
    ) => {
        update(LocalStorageKey.USER_LOCAL_SETTINGS, {
            jobReviewPanelWidth: refToElement.style.width
        })
    }

    const renderBody = () => {
        if (hasResult(jobIDFetch) && jobIDFetch.isError) {
            return (
                <NothingMatched
                    anchor="maincontent"
                    messageVariant="maybe-try"
                    noFilterLink={appDetailPath(orgUsername, appID, {
                        openDetail: true,
                        filter: [],
                        sort: filterAndSort.sort,
                        clearFilterOnCloseModal
                    })}
                />
            )
        }

        if (
            versionsList === undefined ||
            !hasValue(jobIDFetch) ||
            !hasValue(jobFetch) ||
            !hasValue(templateFetch) ||
            !hasValue(instanceFetch) ||
            !hasValue(schemaFetch) ||
            !hasValue(rolesFetch) ||
            (jobFetch.value.status === JobStatus.COMPLETED &&
                !hasValue(jobResponseFetch))
        ) {
            return undefined
        }

        const jobDetailViewProps: JobDataDetailViewProps = {
            job: jobFetch.value,
            instance: instanceFetch.value,
            versionsList,
            selectedVersion,
            prelabels,
            orgUsername,
            appID,
            showOldVersion: versionsList.length > 1 && selectedVersion > 0,
            postProcessedData:
                templateFetch.value.metadata.post_process_data ??
                "not_available",
            postProcessTitle: templateFetch.value.metadata.post_process_title,
            isReviewView,
            dataTab,
            onChangeDataTab: handleChangeDataTab,
            onClickReviewView: handleReviewView,
            role: getValue(rolesFetch) ?? [],
            onEnqueue: handleEnqueue,
            onRelabel: handleRelabel,
            handleCancel,
            urlSigner,
            inputSchema: schemaFetch.value.input,
            outputSchema: schemaFetch.value.output,
            onItemChange: handleItemChange,
            onReviewChange: handleReviewChange,
            onEditAttempt: highlightEdit,
            onShortcutList: setShortcutList
        }

        return (
            <>
                {!isReviewView && (
                    <div className="px-6 pt-3">
                        <JobDetailViewHeader
                            job={jobFetch.value}
                            instance={instanceFetch.value}
                            taskList={taskList}
                            onSelectTask={handleTaskOnChange}
                        />
                    </div>
                )}
                <div
                    id="maincontent"
                    className={`w-full flex-grow ${
                        isReviewView ? "h-full" : "flex px-6 py-3 min-h-0"
                    }`}
                >
                    <div
                        className={`bg-white border border-rhino-300 ${
                            isReviewView
                                ? "h-full w-full"
                                : "shadow-md rounded-lg min-h-0 mr-1"
                        }`}
                        style={
                            !isReviewView
                                ? {
                                      width: "100%",
                                      minWidth: "calc(50% - 0.25rem)"
                                  }
                                : undefined
                        }
                    >
                        <div
                            className="pb-0 h-full flex flex-col"
                            data-testid="detail-view-body"
                        >
                            <JobDataDetailView
                                key={`${jobID}_${isReviewView}`}
                                {...jobDetailViewProps}
                            />
                        </div>
                    </div>
                    {!isReviewView && (
                        <Resizable
                            minWidth="20%"
                            maxWidth="50%"
                            defaultSize={{
                                height: "100%",
                                width: jobReviewPanelWidth
                            }}
                            onResize={handlePanelResize}
                            enable={{
                                top: false,
                                right: false,
                                bottom: false,
                                left: true,
                                topRight: false,
                                bottomRight: false,
                                bottomLeft: false,
                                topLeft: false
                            }}
                            className="bg-white border border-rhino-300 shadow-md rounded-lg min-h-0"
                        >
                            <JobDetailViewSidebar
                                appID={appID}
                                job={jobFetch.value}
                                taskList={taskList}
                                onTaskChange={handleTaskOnChange}
                                versionsList={versionsList}
                            />
                        </Resizable>
                    )}
                </div>
                <DetailViewFooter
                    isTaskView={false}
                    appID={appID}
                    job={getValue(jobFetch)}
                    onEdit={
                        currentJob?.isLatestVersion && !isClassificationApp // job edit for classification app TBD
                            ? onEditOutput
                            : undefined
                    }
                    onRelabelRequest={
                        hasAppPermission(
                            getValue(rolesFetch) || [],
                            "job:relabel"
                        ) &&
                        currentJob?.isLatestVersion &&
                        selectedVersion === 0 &&
                        hasValue(jobFetch) &&
                        isRelabelableJob(jobFetch.value)
                            ? confirmRelabel
                            : undefined
                    }
                    onDelete={
                        hasAppPermission(
                            getValue(rolesFetch) || [],
                            "job:delete"
                        ) && !isJobRelabeled
                            ? confirmDelete
                            : undefined
                    }
                    onMessages={() => {}}
                    onAddTag={handleAddTag}
                    onRemoveTag={handleRemoveTag}
                    correct={isReviewView ? undefined : jobFetch.value.correct}
                    onAction={
                        isReviewView
                            ? handleReviewAction
                            : handleCorrectnessChange
                    }
                    positiveLabel={isReviewView ? "Complete" : "Correct"}
                    positiveOngoingLabel="Saving..."
                    negativeLabel={isReviewView ? "Cancel" : "Incorrect"}
                    negativeOngoingLabel={
                        isReviewView ? "Cancelling.." : "Saving..."
                    }
                    enablePositiveAction={
                        isReviewView
                            ? hasReviewChanged
                            : jobFetch.value.correct !== true
                    }
                    enableNegativeAction={
                        isReviewView || jobFetch.value.correct !== false
                    }
                    isReviewView={isReviewView}
                    highlightEdit={isHighlightEdit}
                    shortcutList={shortcutList}
                />
            </>
        )
    }

    return (
        <DataAccessContext.Provider value={dataAccess}>
            <JobBase
                job={getValue(jobFetch)}
                dialog={dialog}
                isDownloading={isDownloading}
                downloadType={downloadType}
                allowResponseDataDownload={allowResponseDataDownload}
                handleDownloadType={setDownloadType}
                handleRelabel={handleRelabel}
                handleCancel={handleCancel}
                handleDelete={handleDelete}
                handleEnqueue={handleEnqueue}
                handleDownload={handleDownload}
                hideDialog={hideDialog}
            >
                <div className="h-full flex flex-col">
                    <SkipNavigationLink />
                    <JobPanelNavHeader
                        mode={
                            navigationItems === undefined ? "all" : "selected"
                        }
                        sort={sortOptions}
                        filter={filterOptions}
                        versionsList={versionsList || []}
                        selectedVersion={selectedVersion}
                        cursor={navigation?.cursor}
                        onPrev={navigation?.onPrev ? handleNavPrev : undefined}
                        onNext={navigation?.onNext ? handleNavNext : undefined}
                        onClearSelection={onClearSelection}
                        onClose={handleClose}
                        disableSortAndFilter={isJobRelabeled}
                        onChangeSelectedVersion={setVersion}
                    />
                    <LoadingContainer
                        defaultSize={{ height: 480 }}
                        className="flex-grow flex flex-col w-full overflow-hidden"
                    >
                        {renderBody()}
                    </LoadingContainer>
                    <NavPrompt
                        navigate={navigate}
                        shouldBlockNavigation={shouldBlock}
                    />
                </div>
            </JobBase>
        </DataAccessContext.Provider>
    )
}
